import { z } from 'zod';

/** One error envelope for every API error (AllExceptionsFilter) and every client-side handler. */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'CSRF_INVALID',
  'INVALID_CREDENTIALS',
  'EMAIL_NOT_VERIFIED',
  'ACCOUNT_LOCKED',
  'TOKEN_INVALID',
  'RATE_LIMITED',
  'DELETE_BLOCKED',
  'LAST_OWNER',
  'JIRA_NOT_CONFIGURED',
  'JIRA_FAILED',
  'BUSY',
  'NOTHING_TO_SYNC',
  'INTERNAL',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Kinds of related records that can block a delete (FR-WS-012); translated as `related.<entity>`. */
export const RELATED_ENTITIES = [
  'projects',
  'tasks',
  'notes',
  'products',
  'timesheets',
  'support',
  'worklogs',
] as const;
export type RelatedEntity = (typeof RELATED_ENTITIES)[number];

export const deleteBlockedDetailsSchema = z.object({
  related: z.array(z.object({ entity: z.enum(RELATED_ENTITIES), count: z.number().int().min(1) })),
});
export type DeleteBlockedDetails = z.infer<typeof deleteBlockedDetailsSchema>;

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/** HTTP status for each code — used by the API filter and by tests. */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  CSRF_INVALID: 403,
  INVALID_CREDENTIALS: 401,
  EMAIL_NOT_VERIFIED: 403,
  ACCOUNT_LOCKED: 429,
  TOKEN_INVALID: 400,
  RATE_LIMITED: 429,
  DELETE_BLOCKED: 409,
  LAST_OWNER: 409,
  /** The workspace has no usable Jira connection (no token). */
  JIRA_NOT_CONFIGURED: 409,
  /** Jira answered with an error or could not be reached during a sync. */
  JIRA_FAILED: 502,
  /** The same job (e.g. a Jira load) is already running for this workspace. */
  BUSY: 409,
  /** A sync has nothing to ask Jira for (no project has a Jira key, FR-BIZ-002). */
  NOTHING_TO_SYNC: 409,
  INTERNAL: 500,
};
